/**
 * Dosimetry report — Page 1.
 *
 * Header, patient information, liver segmentation volumetry, and interactive 3D viewport.
 */

import { SEGMENT_COLORS, TUMOR_COLOR, ROMAN_NUMERALS, rgbToCss } from '../../domain/segment-colors';
import type { DosimetryStateSnapshot } from '../../hooks/use-dosimetry-state';

import { PatientField, ReportPage, useReportViewport } from '@/components/report';

// ============================================================================
// Types
// ============================================================================

export interface DosimetryReportData {
    patientName: string;
    patientId: string;
    dateOfBirth: string;
    gender: string;
    studyDate: string;
    studyDescription: string;
}

interface DosimetryReportPage1Props {
    data: DosimetryReportData;
    onUpdate: (patch: Partial<DosimetryReportData>) => void;
    state: DosimetryStateSnapshot;
    totalPages: number;
}

export const DOSI_REPORT_VIEWPORT_ID = 'dosi-report-3d-view';

// ============================================================================
// Component
// ============================================================================

export function DosimetryReportPage1({ data, onUpdate, state, totalPages }: DosimetryReportPage1Props) {
    const { containerRef } = useReportViewport({
        viewportId: DOSI_REPORT_VIEWPORT_ID,
    });

    const totalLiver = Array.from(state.segmentVolumes.values()).reduce((a, b) => a + b, 0);

    return (
        <ReportPage pageNumber={1} totalPages={totalPages}>
            {/* ── Header image ── */}
            <div
                className="absolute top-0 left-0 right-0 h-24 bg-top bg-no-repeat bg-cover z-0"
                style={{ backgroundImage: 'url(/report/header.png)' }}
            />

            {/* ── Title ── */}
            <div className="text-center mb-4 z-10">
                <h1 className="text-3xl font-bold text-primary">AI Dosimetry Report</h1>
                <p className="text-sm text-gray-500 mt-1">Liver Radioembolization Planning</p>
            </div>

            {/* ── Patient Information ── */}
            <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-300">
                Patient Information
            </h2>
            <div className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2">
                <PatientField
                    label="Patient Name"
                    value={data.patientName}
                    onChange={(v) => onUpdate({ patientName: v })}
                />
                <PatientField
                    label="Patient ID"
                    value={data.patientId}
                    onChange={(v) => onUpdate({ patientId: v })}
                />
                <PatientField
                    label="DOB / Age"
                    value={data.dateOfBirth}
                    onChange={(v) => onUpdate({ dateOfBirth: v })}
                />
                <PatientField label="Gender" value={data.gender} onChange={(v) => onUpdate({ gender: v })} />
                <PatientField
                    label="Study Date"
                    value={data.studyDate}
                    onChange={(v) => onUpdate({ studyDate: v })}
                />
                <PatientField
                    label="Study Description"
                    value={data.studyDescription}
                    onChange={(v) => onUpdate({ studyDescription: v })}
                />
            </div>

            {/* ── Two-column: Volumetry table | 3D Viewport ── */}
            <div className="flex flex-1 gap-4 min-h-0">
                {/* Left: Volumetry */}
                <div className="flex-1 min-h-0 self-center overflow-hidden">
                    <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                        Liver Volumetry
                    </h3>

                    {/* Segments */}
                    <div className="flex flex-col gap-1">
                        {Array.from(state.segmentVolumes.entries())
                            .sort(([a], [b]) => a - b)
                            .map(([lv, vol]) => {
                                const color = SEGMENT_COLORS[lv] ?? [0.6, 0.6, 0.6];
                                return (
                                    <div key={lv} className="flex items-center gap-2 text-[10px]">
                                        <span
                                            className="h-2.5 w-2.5 shrink-0 rounded-sm"
                                            style={{ backgroundColor: rgbToCss(color) }}
                                        />
                                        <span className="font-mono font-medium w-8">
                                            {ROMAN_NUMERALS[lv]}
                                        </span>
                                        <span className="ml-auto tabular-nums font-mono text-gray-600">
                                            {vol.toFixed(1)} mL
                                        </span>
                                    </div>
                                );
                            })}
                    </div>

                    {/* Tumors */}
                    {state.tumorVolumes.size > 0 && (
                        <div className="mt-3 pt-2 border-t border-gray-200">
                            <div className="flex flex-col gap-1">
                                {Array.from(state.tumorVolumes.entries())
                                    .sort(([a], [b]) => a - b)
                                    .map(([lv, vol]) => (
                                        <div key={lv} className="flex items-center gap-2 text-[10px]">
                                            <span
                                                className="h-2.5 w-2.5 shrink-0 rounded-sm"
                                                style={{ backgroundColor: rgbToCss(TUMOR_COLOR) }}
                                            />
                                            <span className="font-mono font-medium">Tumor {lv}</span>
                                            <span className="ml-auto tabular-nums font-mono text-gray-600">
                                                {vol.toFixed(1)} mL
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Total */}
                    <div className="mt-3 pt-2 border-t-2 border-gray-300 text-[10px] font-bold flex justify-between">
                        <span>Total Liver Volume</span>
                        <span className="tabular-nums font-mono">{totalLiver.toFixed(1)} mL</span>
                    </div>
                </div>

                {/* Right: 3D Viewport */}
                <div
                    ref={containerRef}
                    data-report-viewport
                    className="relative flex-1 self-center min-h-0 overflow-hidden rounded"
                    style={{ backgroundColor: '#f5f5f4' }}
                />
            </div>
        </ReportPage>
    );
}
